import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Button,
  Stepper,
  Step,
  StepLabel,
  DialogContent,
  Divider,
  DialogActions,
  Typography,
  useTheme
} from '@mui/material';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import Loader from 'src/common/Loader';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { useDispatch, useSelector } from 'react-redux';
import { APIService } from 'src/api/api';
import { getAllTanks } from 'src/redux/tankSlice';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import { getAllSites } from 'src/redux/siteSlice';
import {
  addNewTransaction,
  updateTransactionDetails
} from 'src/redux/transactionSlice';
import { TRANSACTION_TYPES } from 'src/utils/constants';
import useAuth from 'src/context/AuthContext';
import { getFuelVolumeUnitLabel, hasConfiguredFuelVolumeUnit } from 'src/utils/volumeUtils';
import TransactionDetailsForm from './forms/TransactionDetailsForm';
import AdditionalDetailsForm from './forms/AdditionalDetailsForm';
import validationSchema from './formModel/transactionValidation';
import transactionFormModel from './formModel/transactionFormModel';
import formInitialValues from './formModel/transactionInitialValues';
import ExternalTransactionForm from './forms/ExternalTransactionForm';
import externalTransactionFormModel from './formModel/externalTransactionFormModel';
import externalTransactionInitialValues from './formModel/externalTransactionInitialValues';
import externalTransactionValidation from './formModel/externalTransactionValidation';
import { TRANSACTION_STYLE_COLORS } from './transactionStyleTokens';

const { formId, formField } = transactionFormModel;
const {
  formId: externalFormId,
  formField: externalFormField
} = externalTransactionFormModel;

const getNormalizedTextValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (typeof value === 'object') {
    return value.name
      || value.station_name
      || value.site_name
      || value.label
      || '';
  }
  return '';
};

function renderStepContent(step, initialValues, isExternal, extraProps = {}) {
  if (isExternal) {
    return <ExternalTransactionForm formField={externalFormField} initialValues={initialValues} {...extraProps} />;
  }
  switch (step) {
    case 0:
      return <TransactionDetailsForm formField={formField} initialValues={initialValues} {...extraProps} />;
    case 1:
      return <AdditionalDetailsForm formField={formField} {...extraProps} />;
    default:
      return null;
  }
}

const NewTransaction = ({
  open,
  handleClose,
  initialValues,
  transactionType: propTransactionType
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const user = getUser();
  const [loading, setLoading] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const { transactionType: reduxTransactionType } = useSelector((state) => state.transactions);
  const transactionType = propTransactionType || reduxTransactionType;
  const isExternal = transactionType === TRANSACTION_TYPES[3].id;
  const isInward = transactionType === TRANSACTION_TYPES[1].id;
  const isEditMode = Boolean(initialValues);
  const hasConfiguredVolumeUnit = hasConfiguredFuelVolumeUnit(user);
  const requiredHintText = '';

  const steps = isExternal
    ? ['Transaction Details']
    : ['Transaction Details', 'Additional Details'];
  const isLastStep = activeStep === steps.length - 1;
  const currentValidationSchema = isExternal ? externalTransactionValidation : validationSchema[activeStep];
  const formInitValues = useMemo(() => {
    if (isExternal) {
      return {
        ...externalTransactionInitialValues,
        ...(initialValues || {})
      };
    }
    if (!initialValues) return formInitialValues;

    const inwardVolumeFields = [
      'quantity',
      'delivered_dip_quantity',
      'delivered_measured_quantity_FLS',
      'delivered_measured_quantity_meter',
      'spillage_quantity',
      'shortage_quantity'
    ];
    const configuredInwardValues = isInward && hasConfiguredVolumeUnit
      ? inwardVolumeFields.reduce((values, field) => {
        const configuredField = `${field}_gallons`;
        if (initialValues?.[configuredField] !== undefined && initialValues?.[configuredField] !== null) {
          return { ...values, [field]: initialValues[configuredField] };
        }
        return values;
      }, {})
      : {};

    return {
      ...formInitialValues,
      ...initialValues,
      ...configuredInwardValues,
      [formField.receivedStation.name]: getNormalizedTextValue(initialValues[formField.receivedStation.name]),
      [formField.remarks.name]: getNormalizedTextValue(initialValues[formField.remarks.name]),
    };
  }, [hasConfiguredVolumeUnit, initialValues, isExternal, isInward]);

  useEffect(() => {
    let isMounted = true;
    if (open && isInward) {
      dispatch(getAllTanks());
      dispatch(getAllSites());
      setSupplierOptions([]);
      APIService.getAllSuppliers().then((response) => {
        if (isMounted && response?.success) {
          setSupplierOptions((response.result || []).map((supplier) => ({
            id: supplier.name,
            name: supplier.name
          })));
        }
      });
    }
    if (open && isExternal) {
      dispatch(getAllSites());
    }
    setActiveStep(0);
    return () => {
      isMounted = false;
    };
  }, [dispatch, isExternal, isInward, open]);

  const handleBack = () => {
    if (activeStep === 0) {
      handleClose();
    } else {
      setActiveStep(activeStep - 1);
    }
  };

  const submitForm = async (values, actions) => {
    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(
          updateTransactionDetails({ transactionType, values })
        ).unwrap();
      } else {
        const payload = isExternal
          ? values
          : { ...values, actual_filled: values.quantity - values.shortage_quantity };
        await dispatch(
          addNewTransaction({ transactionType, values: payload })
        ).unwrap();
      }

      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Transaction updated successfully'
            : 'Transaction created successfully'
        })
      );

      handleClose();
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      setActiveStep((prev) => prev + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const getActionButtonLabel = () => {
    if (!isLastStep) return 'Next';
    if (isExternal) return initialValues ? 'Update Transaction' : 'Add Transaction';
    return initialValues ? 'Update Transaction' : 'Create Transaction';
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'Edit Transaction' : 'Add New Transaction'}
        onCancel={handleClose}
      />

      <Divider />

      <DialogContent sx={{ p: 3 }}>
        {!isEditMode && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            * Required fields
          </Typography>
        )}
        {!loading && !isExternal && (
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 4,
              '& .MuiStepLabel-label': {
                fontWeight: 600,
                color: TRANSACTION_STYLE_COLORS.muted
              },
              '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
                color: TRANSACTION_STYLE_COLORS.foreground
              },
              '& .MuiStepIcon-root': {
                color: TRANSACTION_STYLE_COLORS.border
              },
              '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
                color: TRANSACTION_STYLE_COLORS.primary
              },
              '& .MuiStepConnector-line': {
                borderColor: TRANSACTION_STYLE_COLORS.border
              },
              '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                borderColor: TRANSACTION_STYLE_COLORS.primary
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        <Formik
          initialValues={formInitValues}
          enableReinitialize
          validationSchema={currentValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (loading ? (
            <Loader />
          ) : (
            <Form id={isExternal ? externalFormId : formId}>
              {renderStepContent(activeStep, initialValues, isExternal, {
                requiredHintText,
                supplierOptions,
                isInward,
                volumeUnitLabel: hasConfiguredVolumeUnit ? getFuelVolumeUnitLabel(user) : ''
              })}

              <DialogActions
                sx={{
                  mt: 4,
                  pt: 2,
                  borderTop: `1px solid ${theme.palette.divider}`
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'hsl(198 87% 32%)',
                    color: 'hsl(198 87% 32%)',
                    '&:hover': {
                      borderColor: 'hsl(198 87% 32%)',
                      backgroundColor: 'rgba(11, 106, 153, 0.06)'
                    }
                  }}
                >
                  {activeStep === 0 ? 'Cancel' : 'Back'}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    backgroundColor: 'hsl(198 87% 32%)',
                    borderColor: 'hsl(198 87% 32%)',
                    color: '#ffffff',
                    boxShadow: '0 1px 2px rgb(15 23 42 / 0.06)',
                    '&:hover': {
                      backgroundColor: 'hsl(198 87% 32%)',
                      borderColor: 'hsl(198 87% 32%)',
                      filter: 'brightness(0.95)'
                    }
                  }}
                >
                  {getActionButtonLabel()}
                </Button>
              </DialogActions>
            </Form>
          ))}
        </Formik>
      </DialogContent>
    </FMSModal>
  );
};

NewTransaction.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  transactionType: PropTypes.string
};

NewTransaction.defaultProps = {
  initialValues: null,
  transactionType: ''
};

export default NewTransaction;
